import { Collider, ColliderShape } from '../components/Collider';
import { type EngineContext } from '../core/EngineContext';
import { type CollisionEventPayload } from '../core/EngineEvents';
import { SystemStage, type System } from '../core/System';
import { type Entity } from '../entities/Entity';
import { type EntityId } from '../entities/EntityId';
import { WorldTransform } from '../entities/WorldTransform';
import { circleIntersectsRect, circlesIntersect } from '../math/Intersection';
import { type Scene } from '../scene/Scene';
import { createWorldColliderShape, fillWorldCollider, type WorldColliderShape } from './ColliderGeometry';

/**
 * Upper bound on an entity id that can take part in a collision pair.
 *
 * Overlap is remembered as one number per pair, `firstId * stride + secondId`, which stays exact while
 * both ids fit below the stride. A scene would have to create a million entities in one session to
 * reach it, and doing so would break far more than this bookkeeping.
 */
const PAIR_ID_STRIDE = 1_000_000;

/** One collider resolved into world space, ready to test against every other. */
interface WorldCollider extends WorldColliderShape {
  /** Entity the collider belongs to. */
  readonly entityId: EntityId;
  /** Bit mask of the groups this collider belongs to. */
  readonly group: number;
  /** Bit mask of the groups this collider tests against. */
  readonly mask: number;
}

/**
 * Finds which colliders overlap and announces the changes.
 *
 * A system, not a behavior, and the clearest case of the distinction in the engine: an overlap is not
 * something either entity can know on its own. Reacting to it is a behavior's job, which is why nothing
 * here moves anything.
 *
 * The broadphase is every pair against every other. For scenes of tens of entities that is a few
 * hundred cheap tests a frame and a grid would cost more to maintain than it saved; when a game
 * genuinely needs thousands, a spatial hash goes behind this same interface without touching a game.
 *
 * Only changes are announced. Overlapping pairs are remembered between frames so `collisionEnter` and
 * `collisionExit` fire once each, rather than a behavior having to work out when a continuous overlap
 * started. A destroyed entity's pairs exit like any other, because they simply stop being found.
 */
export class CollisionSystem implements System {
  /** Collision runs after behaviors have moved everything for the frame. */
  readonly stage = SystemStage.Collision;

  readonly #overlapping = new Map<number, CollisionEventPayload>();
  readonly #currentOverlaps = new Map<number, CollisionEventPayload>();
  readonly #worldTransform = new WorldTransform();

  #events: EngineContext['events'] | undefined;

  /** Captures the event bus the collisions are published on. */
  onRegister(context: EngineContext): void {
    this.#events = context.events;
  }

  /** Tests every eligible pair and emits the enter and exit events for this frame. */
  onUpdate(scene: Scene, _deltaSeconds: number): void {
    const colliders = this.#collectColliders(scene);
    this.#currentOverlaps.clear();

    for (let i = 0; i < colliders.length; i += 1) {
      for (let j = i + 1; j < colliders.length; j += 1) {
        const first = colliders[i];
        const second = colliders[j];
        if (first === undefined || second === undefined || !groupsMatch(first, second) || !overlaps(first, second)) {
          continue;
        }

        const payload = toPayload(first.entityId, second.entityId);
        const pair = toPairKey(payload);
        this.#currentOverlaps.set(pair, payload);
        if (!this.#overlapping.has(pair)) {
          this.#events?.emit('collisionEnter', payload);
        }
      }
    }

    this.#emitExits();
  }

  /** Forgets every remembered overlap, so a re-registered system starts from a clean slate. */
  onUnregister(): void {
    this.#overlapping.clear();
    this.#currentOverlaps.clear();
    this.#events = undefined;
  }

  #emitExits(): void {
    for (const [pair, payload] of this.#overlapping) {
      if (this.#currentOverlaps.has(pair)) {
        continue;
      }
      this.#overlapping.delete(pair);
      this.#events?.emit('collisionExit', payload);
    }

    for (const [pair, payload] of this.#currentOverlaps) {
      this.#overlapping.set(pair, payload);
    }
  }

  #collectColliders(scene: Scene): readonly WorldCollider[] {
    const resolved: WorldCollider[] = [];

    for (const entity of scene.getEntitiesWith(Collider)) {
      const collider = entity.getComponent(Collider);
      if (collider === undefined || !collider.enabled || !entity.activeInHierarchy) {
        continue;
      }
      resolved.push(this.#toWorldCollider(entity, collider));
    }

    return resolved;
  }

  /*
   * A fresh shape per collider, because every one of them has to survive until the pair loop is done.
   * The transform is the reused instance, which is safe: the geometry is copied out before the next
   * entity overwrites it.
   */
  #toWorldCollider(entity: Entity, collider: Collider): WorldCollider {
    const world = entity.getWorldTransform(this.#worldTransform);
    const geometry = fillWorldCollider(createWorldColliderShape(), world, collider);

    return { ...geometry, entityId: entity.id, group: collider.group, mask: collider.mask };
  }
}

/** A pair collides only when each side's mask accepts the other's group. */
function groupsMatch(first: WorldCollider, second: WorldCollider): boolean {
  return (first.group & second.mask) !== 0 && (second.group & first.mask) !== 0;
}

function overlaps(first: WorldCollider, second: WorldCollider): boolean {
  if (first.shape === ColliderShape.Circle && second.shape === ColliderShape.Circle) {
    return circlesIntersect(first, second);
  }
  if (first.shape === ColliderShape.Circle) {
    return circleIntersectsRect(first, second.bounds);
  }
  if (second.shape === ColliderShape.Circle) {
    return circleIntersectsRect(second, first.bounds);
  }
  return first.bounds.intersects(second.bounds);
}

function toPairKey(payload: CollisionEventPayload): number {
  return payload.firstId * PAIR_ID_STRIDE + payload.secondId;
}

/** Orders the pair by id, so the same two entities always produce the same payload and the same key. */
function toPayload(first: EntityId, second: EntityId): CollisionEventPayload {
  return first <= second ? { firstId: first, secondId: second } : { firstId: second, secondId: first };
}
